import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  type?: ToastType;
  title?: string;
  duration?: number; // ms, default 4000; pass 0 to persist until dismissed
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  title?: string;
  duration: number;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'default';
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

interface AlertContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextValue | null>(null);

let toastCounter = 0;

export function AlertProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const timeoutsRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = `toast-${++toastCounter}`;
      const toast: Toast = {
        id,
        message,
        type: options.type ?? 'info',
        title: options.title,
        duration: options.duration ?? 4000,
      };
      setToasts((prev) => [...prev, toast]);
      if (toast.duration > 0) {
        const timeoutId = window.setTimeout(() => dismissToast(id), toast.duration);
        timeoutsRef.current.set(id, timeoutId);
      }
    },
    [dismissToast]
  );

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...options, resolve });
    });
  }, []);

  const handleConfirmClose = useCallback(
    (result: boolean) => {
      if (confirmState) {
        confirmState.resolve(result);
        setConfirmState(null);
      }
    },
    [confirmState]
  );

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      timeouts.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast, confirm }), [showToast, confirm]);

  return (
    <AlertContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {confirmState && (
        <ConfirmDialog options={confirmState} onClose={handleConfirmClose} />
      )}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return ctx;
}

const toastStyles: Record<
  ToastType,
  { icon: typeof CheckCircle2; ring: string; bg: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle2,
    ring: 'ring-success-100',
    bg: 'bg-white',
    iconColor: 'text-success-600',
  },
  error: {
    icon: AlertCircle,
    ring: 'ring-error-100',
    bg: 'bg-white',
    iconColor: 'text-error-600',
  },
  warning: {
    icon: AlertTriangle,
    ring: 'ring-warning-100',
    bg: 'bg-white',
    iconColor: 'text-warning-600',
  },
  info: {
    icon: Info,
    ring: 'ring-primary-100',
    bg: 'bg-white',
    iconColor: 'text-primary-600',
  },
};

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  if (typeof document === 'undefined') return null;
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed top-4 right-4 z-[2000] flex flex-col gap-2 w-[min(92vw,360px)] pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const s = toastStyles[toast.type];
        const Icon = s.icon;
        return (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto ${s.bg} ${s.ring} ring-1 rounded-lg shadow-lg border border-neutral-200 p-3 flex items-start gap-3 animate-[slideInRight_0.2s_ease-out]`}
          >
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.iconColor}`} aria-hidden="true" />
            <div className="flex-1 min-w-0">
              {toast.title && (
                <div className="text-sm font-semibold text-neutral-900 mb-0.5">
                  {toast.title}
                </div>
              )}
              <div className="text-sm text-neutral-700 break-words">{toast.message}</div>
            </div>
            <button
              onClick={() => onDismiss(toast.id)}
              className="flex-shrink-0 p-1 -m-1 text-neutral-400 hover:text-neutral-600 rounded transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
}

function ConfirmDialog({
  options,
  onClose,
}: {
  options: ConfirmOptions;
  onClose: (result: boolean) => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const isDanger = options.variant === 'danger';

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 bg-black bg-opacity-50 z-[2100] flex items-center justify-center p-4"
      onClick={() => onClose(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose(false);
        if (e.key === 'Enter') onClose(true);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="bg-white rounded-lg shadow-2xl max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 id="confirm-title" className="text-lg font-semibold text-neutral-900 mb-2">
            {options.title ?? 'Are you sure?'}
          </h3>
          <p className="text-sm text-neutral-600 whitespace-pre-line">{options.message}</p>
        </div>
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex justify-end gap-2 rounded-b-lg">
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 text-neutral-700 hover:bg-neutral-200 rounded-lg transition-colors text-sm font-medium"
          >
            {options.cancelText ?? 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            onClick={() => onClose(true)}
            className={`px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
              isDanger
                ? 'bg-error-600 hover:bg-error-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {options.confirmText ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
